import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase';

// Import our new PublicQuestionnaire component
import PublicQuestionnaire from '../components/PublicQuestionnaire';

// Initialize functions once
const functions = getFunctions(app, "us-central1");

const QuestionnaireAccessPage = () => {
  const { token } = useParams();
  console.log(`[DEBUG] Token from URL: "${token}"`);
  console.log(`[DEBUG] Token length: ${token?.length}`);
  console.log(`[DEBUG] Token type: ${typeof token}`);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State to hold all the data fetched via the token
  const [inviteData, setInviteData] = useState(null); 
  
  // State to manage the submission process
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);

  // Fetch questionnaire data using the token
  const fetchQuestionnaireData = useCallback(async () => {
    if (!token) {
      setError("No invite token provided in the URL.");
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      console.log(`Fetching questionnaire with token: ${token.substring(0, 8)}...`);
      const getQuestionnaireByInviteToken = httpsCallable(functions, "getQuestionnaireByInviteToken");
      console.log(`[DEBUG] About to call function with payload:`, { token });
      const response = await getQuestionnaireByInviteToken({ token });
      
      if (response.data.error) {
        throw new Error(response.data.error);
      }

      console.log("Successfully fetched invite data:", response.data);
      setInviteData(response.data);

    } catch (err) {
      console.error("Error fetching questionnaire data:", err);
      setError(err.message || "An unexpected error occurred. The link may be invalid or expired.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchQuestionnaireData();
  }, [fetchQuestionnaireData]);

  // Handle final submission when CEO completes the questionnaire
const handleFinalSubmit = async (responses, isSubmission = true) => {
  if (isSubmission) {
    // Final submission logic (existing)
    setIsSubmitting(true);
    setError(null);
    try {
      console.log("Submitting questionnaire responses...", responses);
      const submitQuestionnaireFromInvite = httpsCallable(functions, "submitQuestionnaireFromInvite");
      const response = await submitQuestionnaireFromInvite({ token, responses });
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      console.log("Submission successful:", response.data);
      setSubmissionComplete(true);
    } catch (err) {
      console.error("Error submitting questionnaire:", err);
      setError(err.message || "Failed to submit answers. Please try again.");
      setIsSubmitting(false);
    }
  } else {
    // Draft saving logic (NEW)
    try {
      console.log("Saving draft...", responses);
      const submitQuestionnaireFromInvite = httpsCallable(functions, "submitQuestionnaireFromInvite");
      const response = await submitQuestionnaireFromInvite({ 
        token, 
        responses, 
        isDraft: true  // Add this flag
      });
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      console.log("Draft saved successfully:", response.data);
      alert("Work saved successfully!");
    } catch (err) {
      console.error("Error saving draft:", err);
      alert("Failed to save work. Please try again.");
    }
  }
};

  // --- RENDER LOGIC ---

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading Questionnaire...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // Display success message after submission
  if (submissionComplete) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Thank You!
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your responses have been successfully submitted.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You may now close this window. Your responses will be reviewed and processed.
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Render the actual questionnaire
  if (inviteData && inviteData.template) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            {inviteData.template.name || "Strategic Questionnaire"}
          </Typography>
          {inviteData.customText && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Message:</strong> {inviteData.customText}
              </Typography>
            </Alert>
          )}
        </Paper>

        {/* Render the new PublicQuestionnaire component */}
        <PublicQuestionnaire
          template={inviteData.template}
          onSubmitSuccess={handleFinalSubmit}
          isSubmitting={isSubmitting}
          initialResponses={inviteData.existingResponses}
        />
      </Container>
    );
  }

  // Fallback if no data
  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Alert severity="warning">
        Unable to load questionnaire data. Please try again or contact support.
      </Alert>
    </Container>
  );
};

export default QuestionnaireAccessPage;